import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  LayoutDashboard, Tag, FolderOpen, Calculator,
  Phone, RotateCcw, Map, Settings, ChevronLeft, Sun, Moon, Briefcase, BarChart2, Code, Package,
  CreditCard, Gamepad2, PenLine, Activity,
} from 'lucide-react';

const MODULE_ICONS = {
  cotizaciones:   Tag,
  documentos:     FolderOpen,
  cotizador:      Calculator,
  'guia-llamada': Phone,
  devoluciones:   RotateCcw,
  prospeccion:    Map,
  cartera:        Briefcase,
  facturacion:    BarChart2,
  desarrollos:    Code,
  inventario:     Package,
  'calculadora-cuotas': CreditCard,
  arcade:         Gamepad2,
  firmas:         PenLine,
  automatizaciones: Activity,
  admin:          Settings,
};

function SidebarLink({ to, Icon, label, collapsed, exact }) {
  return (
    <NavLink
      to={to}
      end={exact}
      title={collapsed ? label : undefined}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        margin: '2px 8px',
        borderRadius: 8,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? 'var(--accent)' : 'var(--text-2)',
        background: isActive ? 'var(--accent-dim)' : 'transparent',
        justifyContent: collapsed ? 'center' : 'flex-start',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        transition: 'color .15s, background .15s',
      })}
    >
      <Icon size={17} strokeWidth={1.75} style={{ flexShrink: 0 }} />
      {!collapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
    </NavLink>
  );
}

export { MODULE_ICONS };

export default function Sidebar({ isOpen, onClose }) {
  const [collapsed, setCollapsed] = useState(() =>
    localStorage.getItem('rd-sidebar-collapsed') === 'true'
  );
  const { user, effectiveRole, modules } = useAuth();
  const { theme, toggle } = useTheme();
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => { onClose?.(); }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('rd-sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  if (!user) return null;

  const visibleModules = modules.filter(
    m => m.roles.includes(effectiveRole) && m.active !== false
  );
  const showAdmin = effectiveRole === 'admin' || effectiveRole === 'dev';
  // En mobile el drawer siempre se abre expandido (con iconos + labels)
  const isMobile = window.innerWidth <= 768;
  const isCollapsed = isMobile ? false : collapsed;
  const W = isCollapsed ? 64 : 220;

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`sidebar-backdrop ${isOpen ? 'visible' : ''}`}
        onClick={onClose}
      />
    <aside
      className={isOpen ? 'sidebar-open' : ''}
      style={{
        width: W,
        minWidth: W,
        height: '100%',
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width .2s ease, min-width .2s ease',
        overflow: 'hidden',
        flexShrink: 0,
      }}>

      {/* Nav links — scrollable */}
      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto', overflowX: 'hidden' }}>
        <SidebarLink to="/" Icon={LayoutDashboard} label="Dashboard" collapsed={isCollapsed} exact />
        {visibleModules.map(m => {
          const Icon = MODULE_ICONS[m.id];
          if (!Icon) return null;
          return <SidebarLink key={m.id} to={m.path} Icon={Icon} label={m.label} collapsed={isCollapsed} />;
        })}
        {showAdmin && (
          <SidebarLink to="/admin" Icon={Settings} label="Admin" collapsed={isCollapsed} />
        )}
      </nav>

      {/* Bottom actions — siempre al fondo, nunca scrollea */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '8px 0', flexShrink: 0 }}>
        {/* Theme toggle */}
        <button
          onClick={toggle}
          title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          style={{
            width: 'calc(100% - 16px)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 12px',
            margin: '0 8px',
            background: 'none',
            border: 'none',
            borderRadius: 8,
            color: 'var(--text-3)',
            cursor: 'pointer',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            fontFamily: 'inherit',
            fontSize: 13,
            transition: 'background .15s, color .15s',
          }}
        >
          {theme === 'dark'
            ? <Sun size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
            : <Moon size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
          }
          {!isCollapsed && <span>{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>}
        </button>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          className="sidebar-collapse-btn"
          style={{
            width: 'calc(100% - 16px)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 12px',
            margin: '0 8px',
            background: 'none',
            border: 'none',
            borderRadius: 8,
            color: 'var(--text-3)',
            cursor: 'pointer',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
            fontFamily: 'inherit',
            fontSize: 13,
            transition: 'background .15s, color .15s',
          }}
        >
          <ChevronLeft
            size={16}
            strokeWidth={2}
            style={{
              flexShrink: 0,
              transform: isCollapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s',
            }}
          />
          {!isCollapsed && <span>Colapsar</span>}
        </button>
      </div>
    </aside>
    </>
  );
}
